import { Crosshair } from 'lucide-react';
import { VENUE_STATE_LABEL, type VenueReadiness } from './venueReadiness';

/** One line per venue that cannot show rows right now (#904). Rendered above
 * the summary tiles so the figures below are read with their coverage. The
 * readable venue's data is untouched; this is an explanation, not a gate. */
export function VenueCoverageNotice({ kalshi, poly }: { kalshi: VenueReadiness; poly: VenueReadiness }) {
  const lines = [
    ...(kalshi.readable ? [] : [{ venue: 'Kalshi', id: 'kalshi', v: kalshi }]),
    ...(poly.readable ? [] : [{ venue: 'Polymarket', id: 'poly', v: poly }]),
  ];
  if (lines.length === 0) return null;
  const live = kalshi.readable ? 'Kalshi' : poly.readable ? 'Polymarket' : null;
  return (
    <div
      role="status"
      aria-label="Venue coverage"
      data-testid="tracker-venue-notice"
      className="bg-card border-border text-muted-foreground rounded-lg border px-4 py-2 text-xs"
    >
      {lines.map(({ venue, id, v }) => (
        <div key={id} data-testid={`tracker-venue-notice-${id}`} className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-foreground font-medium">{venue} {VENUE_STATE_LABEL[v.state]}</span>
          {v.reason && <span>{v.reason}</span>}
        </div>
      ))}
      {live && (
        <div className="mt-0.5">{live} positions and orders below are live.</div>
      )}
    </div>
  );
}

/** Whole-page placeholder when no venue can show anything. Names each venue's
 * reason so the next step is visible; it never claims a known-flat account. */
export function VenuePlaceholder({ kalshi, poly }: { kalshi: VenueReadiness; poly: VenueReadiness }) {
  return (
    <div
      data-testid="tracker-venue-placeholder"
      className="bg-card border-border text-muted-foreground flex flex-col items-center justify-center gap-3 rounded-lg border px-4 py-8 text-center"
    >
      <Crosshair size={48} strokeWidth={1} />
      <h2 className="text-foreground text-lg font-medium">Position Tracker</h2>
      <p className="text-sm">No venue account is available to read positions from.</p>
      <dl className="grid max-w-xl grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-left text-xs">
        <dt className="text-foreground font-medium">Kalshi</dt>
        <dd>{VENUE_STATE_LABEL[kalshi.state]}{kalshi.reason ? `: ${kalshi.reason}` : ''}</dd>
        <dt className="text-foreground font-medium">Polymarket</dt>
        <dd>{VENUE_STATE_LABEL[poly.state]}{poly.reason ? `: ${poly.reason}` : ''}</dd>
      </dl>
      <p className="text-xs">
        Heads up: Kalshi pauses trading every Thursday 3:00–5:00 AM ET for scheduled
        maintenance — a personal-key connection can fail during that window even when
        your key is fine. Polymarket is unaffected.
      </p>
    </div>
  );
}
